import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6" }
const _hoisted_2 = { class: "bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-2" }
const _hoisted_3 = { class: "flex overflow-x-auto pb-4 gap-4 items-start snap-x snap-mandatory" }
const _hoisted_4 = {
  key: 0,
  class: "w-full text-center text-muted-foreground py-8 border-2 border-dashed rounded-lg"
}
const _hoisted_5 = { class: "p-3 border-b flex items-center justify-between bg-muted/30" }
const _hoisted_6 = ["onUpdate:modelValue"]
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "p-3 flex flex-col gap-3 min-h-[100px]" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = ["onUpdate:modelValue"]
const _hoisted_11 = ["onUpdate:modelValue"]
const _hoisted_12 = { class: "flex items-center gap-2 mt-2 pt-2 border-t border-dashed" }
const _hoisted_13 = { class: "flex flex-col w-1/2" }
const _hoisted_14 = ["onUpdate:modelValue"]
const _hoisted_15 = { class: "flex flex-col w-1/2" }
const _hoisted_16 = ["onUpdate:modelValue"]
const _hoisted_17 = ["onClick"]
const _hoisted_18 = { class: "text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0" }

import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, RefreshCw, Trello, Columns3, CheckCircle2 } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

interface KanbanTask {
  id: string
  desc: string
  assigned: string
  priority: string
}

interface KanbanSection {
  id: string
  title: string
  tasks: KanbanTask[]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'KanbanEditorDialog',
  props: {
    open: { type: Boolean }
  },
  emits: ["update:open", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

// Interfaces
const chartTitle = ref('Desarrollo del Proyecto')
const sections = ref<KanbanSection[]>([
  {
    id: crypto.randomUUID(),
    title: 'Por Hacer (To Do)',
    tasks: [
      { id: 't1', desc: 'Diseñar arquitectura', assigned: 'Alice', priority: 'High' }
    ]
  },
  {
    id: crypto.randomUUID(),
    title: 'En Progreso',
    tasks: [
      { id: 't2', desc: 'Configurar servidor', assigned: 'Bob', priority: 'Medium' }
    ]
  },
  {
    id: crypto.randomUUID(),
    title: 'Hecho (Done)',
    tasks: [
      { id: 't3', desc: 'Reunión inicial', assigned: 'Team', priority: 'Low' }
    ]
  }
])

const generateId = () => crypto.randomUUID()

const addSection = () => {
  sections.value.push({
    id: generateId(),
    title: `Nueva Columna`,
    tasks: []
  })
}

const removeSection = (idx: number) => {
  sections.value.splice(idx, 1)
}

const addTask = (secIdx: number) => {
  const newId = `task_${Math.floor(Math.random() * 10000)}`
  sections.value[secIdx].tasks.push({
    id: newId,
    desc: 'Nueva Tarea',
    assigned: '',
    priority: 'Normal'
  })
}

const removeTask = (secIdx: number, taskIdx: number) => {
  sections.value[secIdx].tasks.splice(taskIdx, 1)
}

// Generar código Mermaid
const generateMermaid = () => {
  let code = `kanban\n`
  if (chartTitle.value.trim()) {
    code += `  title ${chartTitle.value.trim()}\n`
  }
  
  sections.value.forEach(sec => {
    const secTitle = sec.title.trim() || 'Columna'
    code += `  section ${secTitle}\n`
    
    sec.tasks.forEach(t => {
      const safeId = t.id.replace(/[^a-zA-Z0-9_]/g, '') || `id_${Math.floor(Math.random()*1000)}`
      const safeDesc = t.desc.trim().replace(/\[|\]/g, '') || 'Tarea'
      let line = `    ${safeId}[${safeDesc}]`
      
      const meta = []
      if (t.assigned) meta.push(`assigned: '${t.assigned.trim()}'`)
      if (t.priority && t.priority !== 'Normal') meta.push(`priority: '${t.priority.trim()}'`)
      
      if (meta.length > 0) {
        line += `@{ ${meta.join(', ')} }`
      }
      code += `${line}\n`
    })
  })
  
  editableCode.value = code
}

// Parsear desde Mermaid
const syncToVisual = () => {
  const lines = editableCode.value.split('\n')
  const newSections: KanbanSection[] = []
  let newTitle = ''
  
  let currentSec: KanbanSection | null = null
  
  for (const line of lines) {
    const t = line.trim()
    if (!t || t === 'kanban') continue
    
    if (t.startsWith('title ')) {
      newTitle = t.replace('title ', '').trim()
    } else if (t.startsWith('section ')) {
      currentSec = { id: generateId(), title: t.replace('section ', '').trim(), tasks: [] }
      newSections.push(currentSec)
    } else if (currentSec && t.match(/^([a-zA-Z0-9_]+)\[([^\]]+)\]/)) {
      const match = t.match(/^([a-zA-Z0-9_]+)\[([^\]]+)\]/)
      if (match) {
        const taskId = match[1]
        const desc = match[2]
        
        let assigned = ''
        let priority = 'Normal'
        
        const metaMatch = t.match(/@\{\s*(.+?)\s*\}/)
        if (metaMatch) {
          const metaStr = metaMatch[1]
          
          const assignedMatch = metaStr.match(/assigned:\s*'([^']+)'/)
          if (assignedMatch) assigned = assignedMatch[1]
          
          const priorityMatch = metaStr.match(/priority:\s*'([^']+)'/)
          if (priorityMatch) priority = priorityMatch[1]
        }
        
        currentSec.tasks.push({
          id: taskId,
          desc,
          assigned,
          priority
        })
      }
    }
  }
  
  if (newTitle) chartTitle.value = newTitle
  if (newSections.length > 0) sections.value = newSections
}

watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([chartTitle, sections], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseVisualEditor, {
    open: __props.open,
    "onUpdate:open": _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('update:open', $event))),
    title: "Tablero Kanban",
    description: "Organiza tus tareas en columnas para visualizar el flujo de trabajo (Workflow).",
    showCode: showCode.value,
    "onUpdate:showCode": _cache[3] || (_cache[3] = ($event: any) => (showCode.value = $event)),
    copied: copied.value,
    onCopy: copyToClipboard,
    onSave: saveAndClose
  }, {
    icon: _withCtx(() => [
      _createVNode(_unref(Trello), { class: "w-5 h-5 text-primary" })
    ]),
    toolbar: _withCtx(() => [
      _createVNode(_unref(Button), {
        size: "sm",
        variant: "default",
        onClick: addSection
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Columns3), { class: "w-4 h-4 mr-2" }),
          _cache[4] || (_cache[4] = _createTextVNode(" Añadir Columna", -1))
        ]),
        _: 1
      })
    ]),
    canvas: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _cache[5] || (_cache[5] = _createElementVNode("h3", { class: "font-semibold text-lg text-primary" }, "Nombre del Tablero", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((chartTitle).value = $event)),
            class: "bg-background border rounded px-3 py-2 text-base font-bold focus:outline-none focus:ring-2 focus:ring-primary w-full md:w-1/2",
            placeholder: "Ej. Proyecto Alpha"
          }, null, 512), [
            [_vModelText, chartTitle.value]
          ])
        ]),
        _createElementVNode("div", _hoisted_3, [
          (sections.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, " Tablero vacío. Añade una columna para empezar. "))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sections.value, (sec, sIdx) => {
            return (_openBlock(), _createElementBlock("div", {
              key: sec.id,
              class: "bg-card border rounded-lg shadow-sm min-w-[300px] max-w-[320px] flex flex-col snap-center shrink-0"
            }, [
              _createElementVNode("div", _hoisted_5, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((sec.title) = $event),
                  class: "bg-transparent font-bold text-sm focus:outline-none focus:border-b border-primary w-2/3",
                  placeholder: "Nombre Columna"
                }, null, 8, _hoisted_6), [
                  [_vModelText, sec.title]
                ]),
                _createElementVNode("button", {
                  onClick: ($event: any) => (removeSection(sIdx)),
                  class: "text-muted-foreground hover:bg-destructive hover:text-white p-1 rounded transition-colors",
                  title: "Eliminar Columna"
                }, [
                  _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                ], 8, _hoisted_7)
              ]),
              _createElementVNode("div", _hoisted_8, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sec.tasks, (task, tIdx) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: task.id,
                    class: "bg-background border shadow-sm rounded p-3 flex flex-col gap-2 relative group hover:border-primary/50 transition-colors"
                  }, [
                    _createElementVNode("button", {
                      onClick: ($event: any) => (removeTask(sIdx, tIdx)),
                      class: "absolute right-2 top-2 text-muted-foreground hover:bg-destructive hover:text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-all",
                      title: "Eliminar Tarea"
                    }, [
                      _createVNode(_unref(Trash2), { class: "w-3 h-3" })
                    ], 8, _hoisted_9),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": ($event: any) => ((task.id) = $event),
                      class: "bg-transparent text-[10px] font-mono text-muted-foreground uppercase focus:outline-none focus:border-b w-1/3",
                      placeholder: "ID_TAREA"
                    }, null, 8, _hoisted_10), [
                      [_vModelText, task.id]
                    ]),
                    _withDirectives(_createElementVNode("textarea", {
                      "onUpdate:modelValue": ($event: any) => ((task.desc) = $event),
                      class: "bg-transparent text-sm focus:outline-none resize-none font-medium w-full mt-1",
                      placeholder: "Descripción de la tarea...",
                      rows: "2"
                    }, null, 8, _hoisted_11), [
                      [_vModelText, task.desc]
                    ]),
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("div", _hoisted_13, [
                        _cache[6] || (_cache[6] = _createElementVNode("label", { class: "text-[9px] font-bold text-muted-foreground uppercase" }, "Asignado a", -1)),
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((task.assigned) = $event),
                          class: "bg-transparent text-xs focus:outline-none",
                          placeholder: "@usuario"
                        }, null, 8, _hoisted_14), [
                          [_vModelText, task.assigned]
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_15, [
                        _cache[8] || (_cache[8] = _createElementVNode("label", { class: "text-[9px] font-bold text-muted-foreground uppercase" }, "Prioridad", -1)),
                        _withDirectives(_createElementVNode("select", {
                          "onUpdate:modelValue": ($event: any) => ((task.priority) = $event),
                          class: "bg-transparent text-xs focus:outline-none cursor-pointer"
                        }, [...(_cache[7] || (_cache[7] = [
                          _createElementVNode("option", { value: "Normal" }, "Normal", -1),
                          _createElementVNode("option", { value: "High" }, "Alta (High)", -1),
                          _createElementVNode("option", { value: "Medium" }, "Media (Medium)", -1),
                          _createElementVNode("option", { value: "Low" }, "Baja (Low)", -1)
                        ]))], 8, _hoisted_16), [
                          [_vModelSelect, task.priority]
                        ])
                      ])
                    ])
                  ]))
                }), 128)),
                _createElementVNode("button", {
                  onClick: ($event: any) => (addTask(sIdx)),
                  class: "w-full flex items-center justify-center gap-2 py-2 mt-2 text-xs font-semibold text-muted-foreground hover:text-primary hover:bg-muted rounded border border-dashed transition-colors"
                }, [
                  _createVNode(_unref(Plus), { class: "w-3 h-3" }),
                  _cache[9] || (_cache[9] = _createTextVNode(" Añadir Tarea ", -1))
                ], 8, _hoisted_17)
              ])
            ]))
          }), 128))
        ])
      ])
    ]),
    "code-panel": _withCtx(() => [
      _createElementVNode("div", _hoisted_18, [
        _cache[11] || (_cache[11] = _createTextVNode(" Mermaid (Sincronizado) ", -1)),
        _createVNode(_unref(Button), {
          size: "sm",
          variant: "default",
          class: "h-7 text-xs",
          onClick: syncToVisual
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(RefreshCw), { class: "w-3 h-3 mr-2" }),
            _cache[10] || (_cache[10] = _createTextVNode(" Sincronizar a Visual ", -1))
          ]),
          _: 1
        })
      ]),
      _createVNode(MermaidCodeEditor, {
        modelValue: editableCode.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((editableCode).value = $event)),
        class: "flex-1 min-h-0"
      }, null, 8, ["modelValue"])
    ]),
    "live-preview": _withCtx(() => [
      _createVNode(MermaidLivePreview, { code: editableCode.value }, null, 8, ["code"])
    ]),
    _: 1
  }, 8, ["open", "showCode", "copied"]))
}
}

})